<script>
import i18n from "@/plugins/i18n";
import SwitchButton from '../../components/SwitchButton.vue';
import { mapActions } from "vuex";
import MixedIcons from "../../assets/icons/MixedIcons.vue";

export default {
    props: {
        selectedTarget: {
            required: true
        },

        generalColorCode: {
            type: String,
            required: true
        },

        filteredMetrics: {
            required: true
        }
    },

    components: {
        SwitchButton,
        MixedIcons
    },

    data() {
        return {
            loading: false,

            companyType: null,
            supplierTargets: [],
            suppliers: [],

            supplierTargetCreateModal: false,
            supplierCreateTab: "all",
            supplierTargetsForSeparateCreate: [],

            dynamicTarget: {},

            baseYear: null,
            targetYear: null,
            
            baseYearValue: null,
            targetYearValue: null,

            targetYearProgressiveValue: null,

            reportingYearValue: null,
            reportingYearNumericValue: null,

            currentYearValue: null,
            initialCurrentYearValue: null,
            currentYearNumericValue: null,

            textBoxValue: "",
            initialTextBoxValue: "",

            webLinkValue: "",
            initialWebLinkValue: "",

            assignedTargetValue: null,

            supplierTargetToCreate: {
                supplierBaseYear: null,
                supplierTargetYear: null,
                supplierTargetValue: null,
                supplierTargetDescription: null,
                useSupplierName: false
            },

            supplierBaseYear: null,
            supplierTargetYear: null,
            supplierTargetValue: null,
            supplierTargetDescription: null,

            suppliersOwnTargets: [],

            customerTargets: [],

            documentsModal: false,
            readonlyDocumentsModal: false,
            documentsModalYear: null,

            requiredMetrics: [],

            baseYearValuesMatch: true,
            reportingYearValuesMatch: true,
            currentYearValuesMatch: true,
            targetDocumentStatus: []
        }
    },

    watch: {
        selectedTarget(newVal, oldVal) {
            if (newVal.sust_target_id != oldVal.sust_target_id) {
                this._getDefaultDynamicTargetByTargetId();
            }
        },

        assignedTargetValue(newVal) {
            if (newVal == null) {
                this.setDocumentsModal(false);
            }
        },

        targetYearValue(newVal, oldVal) {
            const calculation = this.dynamicTarget.calculation_result_type;
            if (calculation == "TextBox" || this.baseYearValue == null) return;

            if (newVal != oldVal) {
                const diff = (Number(this.targetYearValue) - Number(this.baseYearValue));
                if (calculation == '%') {
                    this.targetYearProgressiveValue = diff * 100 / this.baseYearValue;
                } else {
                    this.targetYearProgressiveValue = diff;
                }
            }
        }
    },

    computed: {
        calculationType() {
            const calc = this.dynamicTarget.calculation_result_type;
            if (calc != "TextBox") return calc;
            else return "-";
        },

        leftTableHeaders() {
            const vals = [
                {val: "base", text: i18n.t('dynamicTargets.base_year')},
                {val: "target", text: i18n.t('dynamicTargets.target_year')}
            ];
            return vals;
        },

        rightTableHeaders() {
            const vals = [
                {val: "reporting", text: i18n.t('dynamicTargets.reporting_year')},
                {val: "current", text: i18n.t('dynamicTargets.current_year')}
            ];
            return vals;
        },

        pastYears() {
            const year = this.currentYear;
            const vals = [
                {text: `${i18n.t('dynamicTargets.select_year')}`, val: null}, 
                {text: `${year-5}`, val: year-5}, 
                {text: `${year-4}`, val: year-4}, 
                {text: `${year-3}`, val: year-3}, 
                {text: `${year-2}`, val: year-2}, 
                {text: `${year-1}`, val: year-1}
            ];
            return vals;
        },

        currentYear() {
            return new Date().getFullYear();
        },

        nextYears() {
            const year = this.currentYear;
            const maxYear = 2055;
            const vals = [{text: `${i18n.t('dynamicTargets.select_year')}`, val: null}];

            for (let i = year; i <= maxYear; i++) {
                vals.push({text: String(i), val: i});
            }

            return vals;
        },

        switchDisabled() {
            if (!this.isTextboxTarget){
                const invalid = this.baseYear == null || this.targetYear == null;   
                return invalid;
            } else {
                const invalid = this.textBoxValue == null || this.textBoxValue.length <= 1;
                return invalid;
            }
        },

        targetValuesMatch() {
            if (this.assignedTargetValue == null) {
                return [true, true, true];
            }

            if (this.assignedTargetValue.hasOwnProperty("assigned_target_id")) {
                return [this.baseYearValuesMatch, this.reportingYearValuesMatch, this.currentYearValuesMatch];
            } else {
                return [true, true, true];
            }
        },

        formattedDynamicTargetName() {
            const name = this.dynamicTarget.dynamic_target_name;

            if (this.assignedTargetValue == null && this.assignedTargetValue == {}) {
                return [name, null];
            }

            if (name.includes('X')) {
                const parts = name.split("X");
                return parts;
            } else {
                return [name, null];
            }
        },

        assignForSuppliersDisabled() {
            const create = this.supplierTargetToCreate;
            if (!this.isTextboxTarget) {
                const valid = (
                    create.supplierBaseYear 
                    && create.supplierTargetYear 
                    && create.supplierTargetValue 
                    && create.supplierTargetDescription
                );

                return !valid;
            } else {
                const valid = (
                    create.supplierTargetYear
                    && create.supplierTargetDescription
                );

                return !valid;
            }
        },

        targetHasNoMatch() {
            const allMatch = !(this.baseYearValuesMatch == false || this.reportingYearValuesMatch == false || this.currentYearValuesMatch == false);
            if (!allMatch) {
                return true;
            } else {
                return false;
            }
        },

        customerTargetHasNoMatch() {
            const targets = this.customerTargets;
            
            if (targets.length > 0){
                let hasNoMatch = false;
                for (const target of targets) {
                    if (this.hasUnmatchingValue(target)) {
                        hasNoMatch = true;
                    }
                }
                return hasNoMatch;
            } else {
                return false;
            }
        },

        // table headers //
        supplierTableHeaders_NotModal() {
            const vals = [
                {flex: .2, text: i18n.t('dynamicTargets.target_year')},
                {flex: .2, text: i18n.t('dynamicTargets.target_value')},
                {flex: .6, text: i18n.t('dynamicTargets.target_description')},
            ];

            return vals;
        },

        supplierCreateTable_Modal() {
            const vals = [
                {flex: .2, text: i18n.t('dynamicTargets.base_year')},
                {flex: .2, text: i18n.t('dynamicTargets.target_year')},
                {flex: .2, text: i18n.t('dynamicTargets.target_value')},
                {flex: .6, text: i18n.t('dynamicTargets.target_description')},
            ];

            return vals;
        },

        supplierTextboxCreateTable_Modal() {
            const vals = [
                {flex: .2, text: i18n.t('dynamicTargets.target_year')},
                {flex: .8, text: i18n.t('dynamicTargets.target_description')},
            ];

            return vals;
        },

        supplierCreateTable_Modal_All() {
            const vals = [
                {flex: .2, text: i18n.t('dynamicTargets.base_year')},
                {flex: .2, text: i18n.t('dynamicTargets.target_year')},
                {flex: .2, text: i18n.t('dynamicTargets.target_value')},
                {flex: .25, text: i18n.t('dynamicTargets.target_description')},
            ];

            return vals;
        },

        supplierTextboxCreateTable_Modal_All() {
            const vals = [
                {flex: .2, text: i18n.t('dynamicTargets.target_year')},
                {flex: .5, text: i18n.t('dynamicTargets.target_description')},
            ];

            return vals;
        },

        supplierCreateTable_Modal_Separate() {
            const vals = [
                {flex: .15, text: i18n.t('dynamicTargets.base_year')},
                {flex: .15, text: i18n.t('dynamicTargets.target_year')},
                {flex: .2, text: i18n.t('dynamicTargets.target_value')},
                {flex: .2, text: i18n.t('dynamicTargets.target_description')},
            ];

            return vals;
        },
        
        supplierTextboxCreateTable_Modal_Separate() {
            const vals = [
                {flex: .2, text: i18n.t('dynamicTargets.target_year')},
                {flex: .4, text: i18n.t('dynamicTargets.target_description')},
            ];

            return vals;
        },

        supplierMonitorTable_Modal() {
            const vals = [
                {flex: 1, text: i18n.t('dynamicTargets.suppliers')},
                {flex: 1, text: i18n.t('dynamicTargets.base_value')},
                {flex: 1, text: i18n.t('dynamicTargets.current_status')},
            ];

            return vals;
        },

        customerTableHeaders() {
            const vals = [
                /* {flex: .1, text: i18n.t('dynamicTargets.dynamic_target_name')}, */
                {flex: .15, text: i18n.t('dynamicTargets.customers')},
                {flex: .1, text: i18n.t('dynamicTargets.base_year')},
                {flex: .15, text: i18n.t('dynamicTargets.base_value')},
                {flex: .15, text: i18n.t('dynamicTargets.target_year')},
                {flex: .15, text: i18n.t('dynamicTargets.target_value')},
                {flex: .3, text: i18n.t('dynamicTargets.target_description')},
            ];

            return vals;
        },
        //////////////

        isTextboxTarget() {
            if (this.dynamicTarget.hasOwnProperty("calculation")) {
                return this.dynamicTarget.calculation.toLowerCase() == "textbox";
            } else {
                return false;
            }
        },

        requiresMetrics() {
            return this.dynamicTarget.calculation.includes("increase") || this.dynamicTarget.calculation.includes("decrease");
        },

        supplierTargetsExists() {
            return this.supplierTargets.length > 0;
        },

        isTurkish() {
            return localStorage.lang.toLowerCase().includes("tr");
        }
    },

    async mounted() {
        await this._getCompaniesCascade();
        await this._getDefaultDynamicTargetByTargetId();
    },

    methods: {
        ...mapActions("company", [
            "getCompaniesCascade"
        ]),

        ...mapActions("supplier", [
            "getDefaultDynamicTargetByTargetId",
            "createAssignedTarget",
            "updateAssignedTarget",
            "deleteAssignedTarget",
            "updateAssignedTargetForSuppliers",
            "getAssignedTargetByTargetId",
            "recalculateAllValues",
            "recalculateBaseYearValue",
            "recalculateReportingYearValue",
            "recalculateCurrentYearValue",
            "calculateYearValue",
            "getCurrentYearProgress",
            "getAssignedTargetsForSuppliers",
            "getAssignedTargetsByAssignee",
            "getSuppliersOwnTargets",
            "createAssignedTargetsForSuppliers"
        ]),

        async _getCompaniesCascade() {
            await this.getCompaniesCascade()
                .then(async (res) => {
                    const amount = res.data.layer_1.length;
                    let role = "";

                    if (amount == 1) {
                        role = res.data.layer_1[0].role;
                        if (role == "regular_company") {
                            this.companyType = "REGULAR";
                            this.suppliers = res.data.layer_2;
                        } else if (role == "supplier") {
                            this.companyType = "SUPPLIER";
                        } else {
                            console.error("Not allowed without being a supplier or primary");
                            return;
                        }
                    } else if (amount > 1) {
                        const supplierIndex = res.data.layer_1.findIndex(item => item.role == "supplier");
                        const regularIndex = res.data.layer_1.findIndex(item => item.role == "regular_company");

                        if (supplierIndex >= 0 && regularIndex >= 0) {
                            this.companyType = "BOTH";
                            this.suppliers = res.data.layer_2;
                        } else if (supplierIndex >= 0 && regularIndex < 0) {
                            this.companyType = "SUPPLIER";
                        } else if (supplierIndex < 0 && regularIndex >= 0) {
                            this.companyType = "REGULAR";
                            this.suppliers = res.data.layer_2;
                        } else {
                            console.error("Not allowed without being a supplier or primary");
                            return;
                        }
                    } else {
                        console.error("No company code found for given company!");
                        return;
                    }
                })
        },

        async _getDefaultDynamicTargetByTargetId() {
            await this.setLoading(true);

            console.log("Company Type: ", this.companyType)

            const params = {
                reference_target_id: this.selectedTarget.sust_target_id,
                is_deleted: false
            }

            await this.getDefaultDynamicTargetByTargetId(params)
                .then( res => {
                    this.dynamicTarget = res.data;
                })
                .catch(({ response }) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "error",
                        duration: 5000,
                        dismissible: true,
                    });
                })
                .finally(async () => {
                    await this.handleFetchDynamicTarget();
                    await this._getAssignedTargetByTargetId();
                    if (this.companyType == "REGULAR") {
                        await this._getAssignedTargetsForSuppliers()
                    } else if (this.companyType == "SUPPLIER") {
                        await this._getAssignedTargetsAsSupplier();
                    } else if (this.companyType == "BOTH") {
                        await this._getAssignedTargetsForSuppliers()
                        await this._getAssignedTargetsAsSupplier();
                    }
                })
        },

        async _getAssignedTargetByTargetId() {
            const params = {
                assigner_company_id: this.$user.company_id,
                assignee_company_id: this.$user.company_id,
                target_id: this.selectedTarget.sust_target_id
            }

            await this.getAssignedTargetByTargetId(params)
                .then( res => {
                    let data;
                    if (res.data.assigned_targets.length > 0) {
                        data = res.data.assigned_targets[0];
                    } else {
                        this.assignedTargetValue = null;
                        return;
                    }
                    
                    this.assignedTargetValue = data;

                    this.baseYearValuesMatch = res.data.values_match.base_year;
                    this.reportingYearValuesMatch = res.data.values_match.reporting_year;
                    this.currentYearValuesMatch = res.data.values_match.current_year;
                    this.targetDocumentStatus = res.data.values_match.document_status;
                    
                    this.targetYear = data.target_year;
                    this.baseYear = data.base_year;
                    
                    this.targetYearValue = data.target_year_value;
                    this.baseYearValue = data.base_year_value == "false" ? null : this.fixFloatingToFour(data.base_year_value);

                    this.reportingYearValue = data.reporting_year_value == "false" ? null : this.fixFloatingToFour(data.reporting_year_value);

                    this.currentYearValue = data.current_year_value == "false" ? null : this.fixFloatingToFour(data.current_year_value);
                    this.initialCurrentYearValue = data.current_year_value;

                    this.textBoxValue = data.text_box;
                    this.initialTextBoxValue = data.text_box;
                    
                    this.webLinkValue = data.web_link;
                    this.initialWebLinkValue = data.web_link;
                })
                .catch( async ({ response }) => {
                    await this.resetAssignedTarget();
                    console.error(this.$parseResMessage(response.data.messageName));
                })
                .finally(async () => {
                    if (this.targetYearValue) {
                        const calculation = this.dynamicTarget.calculation_result_type;
                        if (calculation == "TextBox" || this.baseYearValue == null) return;

                        const diff = (Number(this.targetYearValue) - Number(this.baseYearValue));
                        if (calculation == '%') {
                            this.targetYearProgressiveValue = diff * 100 / this.baseYearValue;
                        } else {
                            this.targetYearProgressiveValue = diff;
                        }
                    }

                    await this.handleTargetHasNoMatch();
                    await this.setLoading(false);
                })
        },

        async _recalculateBaseYearValue() {
            const params = {
                assigned_target_id: this.assignedTargetValue.assigned_target_id
            }

            await this.recalculateBaseYearValue(params)
                .then(response => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "success",
                        duration: 5000,
                        dismissible: true,
                    });
                })
                .catch(({ response }) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "error",
                        duration: 5000,
                        dismissible: true,
                    });
                })
                .finally(async () => {
                    await this._getAssignedTargetByTargetId()
                })
        },

        async _recalculateReportingYearValue() {
            const params = {
                assigned_target_id: this.assignedTargetValue.assigned_target_id
            }

            await this.recalculateReportingYearValue(params)
                .then(response => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "success",
                        duration: 5000,
                        dismissible: true,
                    });
                })
                .catch(({ response }) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "error",
                        duration: 5000,
                        dismissible: true,
                    });
                })
                .finally(async () => {
                    await this._getAssignedTargetByTargetId()
                })
        },

        async _recalculateCurrentYearValue() {
            const params = {
                assigned_target_id: this.assignedTargetValue.assigned_target_id
            }

            await this.recalculateCurrentYearValue(params)
                .then(response => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "success",
                        duration: 5000,
                        dismissible: true,
                    });
                })
                .catch(({ response }) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "error",
                        duration: 5000,
                        dismissible: true,
                    });
                })
                .finally(async () => {
                    await this._getAssignedTargetByTargetId()
                })
        },

        async _recalculateAllValues(update = true) {
            const match = this.targetValuesMatch[0] == true || Number(this.baseYearValue) == 0;

            if (!match) return;

            const params = {
                assigned_target_id: this.assignedTargetValue.assigned_target_id
            }

            await this.recalculateAllValues(params)
                .then(response => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "success",
                        duration: 5000,
                        dismissible: true,
                    });
                })
                .catch(({ response }) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "error",
                        duration: 5000,
                        dismissible: true,
                    });
                })
                .finally(async () => {
                    await this._getAssignedTargetByTargetId();
                })
        },

        async useAsCompanyTarget(state) {
            if (state == true) {
                await this._createAssignedTarget();
            } else if (state == false && this.assignedTargetValue != null) {
                await this._deleteAssignedTarget();
            } else {
                return;
            }
        },

        async _createAssignedTarget() {
            await this.setLoading(true);

            const params = {
                dynamic_target_id: this.dynamicTarget.dynamic_target_id,
                assigner_company_id: this.$user.company_id,
                assignee_company_id: this.$user.company_id,
                reporting_year_value: await this.stringifyNumericValues(this.reportingYearValue),
                base_year: this.baseYear,
                base_year_value: await this.stringifyNumericValues(this.baseYearValue),
                target_year: this.targetYear,
                target_year_value: await this.stringifyNumericValues(this.targetYearValue),
                text_box: String(this.textBoxValue),
                web_link: String(this.webLinkValue),
                created_by: this.$user.user_id
            }

            await this.createAssignedTarget(params)
                .then(response => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "success",
                        duration: 5000,
                        dismissible: true,
                    });
                })
                .catch(({ response }) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "error",
                        duration: 5000,
                        dismissible: true,
                    });
                })
                .finally(async () => {
                    await this._getAssignedTargetByTargetId();
                })
        },

        async _deleteAssignedTarget() {
            const params = {
                assigned_target_id: this.assignedTargetValue.assigned_target_id
            }

            await this.deleteAssignedTarget(params)
                    .then(async (response) => {
                        this.$toast.open({
                            message: this.$parseResMessage(response.data.messageName),
                            type: "success",
                            duration: 5000,
                            dismissible: true,
                        });
                        await this.resetAssignedTarget();
                    })
                    .catch(({ response }) => {
                        this.$toast.open({
                            message: this.$parseResMessage(response.data.messageName),
                            type: "error",
                            duration: 5000,
                            dismissible: true,
                        });
                    })
        },

        async _updateAssignedTarget() {
            if (this.assignedTargetValue == null) return;

            if (this.isTextboxTarget && this.initialTextBoxValue == this.textBoxValue && this.initialWebLinkValue == this.webLinkValue) return;

            let params = {
                assigned_target_id: this.assignedTargetValue.assigned_target_id,
                base_year_value: await this.stringifyNumericValues(this.baseYearValue),
                base_year: this.baseYear,
                target_year_value: await this.stringifyNumericValues(this.targetYearValue),
                target_year: this.targetYear,
                reporting_year_value: await this.stringifyNumericValues(this.reportingYearValue),
                reporting_year: this.currentYear -1,
                text_box: String(this.textBoxValue),
                web_link: String(this.webLinkValue)
            }

            if (this.currentYearValue != this.initialCurrentYearValue) {
                params["current_year_value"] = this.currentYearValue;
            } 

            this.updateAssignedTarget(params)
                .then(async (response) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "success",
                        duration: 5000,
                        dismissible: true,
                    });
                })
                .catch(({ response }) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "error",
                        duration: 5000,
                        dismissible: true,
                    });
                })
                .finally(async () => {
                    await this._getAssignedTargetByTargetId();
                })
        },

        async _getAssignedTargetsForSuppliers() {
            const params = {
                assigner_company_id: this.$user.company_id,
                is_deleted: false
            }

            await this.getAssignedTargetsForSuppliers(params)
                .then( async (res) => {
                    const related_target = res.data.assigned_targets.find(item => this.dynamicTarget.dynamic_target_id == item.dynamic_target_id);
                    
                    if (!related_target || related_target == undefined) {
                        await this.resetSupplierTarget();
                        return;
                    }

                    this.supplierTargets = related_target.companies;
                    this.supplierTargetDescription = related_target.description;
                    this.supplierTargetYear = related_target.target_year;
                    this.supplierTargetValue = related_target.target_year_value;
                    this.supplierBaseYear = related_target.base_year;

                    this.supplierTargetToCreate.supplierTargets = related_target.companies;
                    this.supplierTargetToCreate.supplierTargetDescription = related_target.description;
                    this.supplierTargetToCreate.supplierTargetYear = related_target.target_year;
                    this.supplierTargetToCreate.supplierTargetValue = related_target.target_year_value;
                    this.supplierTargetToCreate.supplierBaseYear = related_target.base_year;
                })
                .catch( async ({ response }) => {
                    await this.resetSupplierTarget();
                    console.error(this.$parseResMessage(response.data.messageName));
                })
                .finally(async () => {
                    await this._getSuppliersOwnTargets();
                })
        },

        async _getAssignedTargetsAsSupplier() {
            const params = {
                assignee_company_id: this.$user.company_id,
                is_deleted: false
            }

            await this.getAssignedTargetsByAssignee(params)
                .then( res => {
                    const data = res.data.assigned_targets.filter(item => item.assigner_company_id != this.$user.company_id && item.dynamic_target_id == this.dynamicTarget.dynamic_target_id);
                    this.customerTargets = data;
                })
                .catch( async ({ response }) => {
                    this.customerTargets = [];
                    console.error(this.$parseResMessage(response.data.messageName));
                })
        },

        async _getSuppliersOwnTargets() {
            const params = {
                assigner_company_id: this.$user.company_id,
                dynamic_target_id: this.dynamicTarget.dynamic_target_id
            }

            await this.getSuppliersOwnTargets(params)
                .then( async (res) => {
                    this.suppliersOwnTargets = res.data.suppliers_own_targets;
                })
                .catch(async ({response}) => {
                    console.error(this.$parseResMessage(response.data.messageName));
                })
        },

        async createTargetForSuppliers() {
            if (this.isTextboxTarget) {
                await this._createTextboxTargetAssignedTargetsForSuppliers();
            } else {
                await this._createAssignedTargetsForSuppliers();
            }
        },
        
        async createTargetForSuppliersSeparately() { // this creates two targets because the request used here is specifically for creating it for all suppliers
            try {
                for (const target of this.supplierTargetsForSeparateCreate) {
                    if (!this.isTextboxTarget) {
                        if (
                            target.target_year == null || 
                            target.target_value == null || 
                            target.base_year == null
                        ) { return; }
                            
                        const params = {
                            dynamic_target_id: this.dynamicTarget.dynamic_target_id,
                            assigner_company_id: this.$user.company_id,
                            assignee_company_id: target.assignee_company_id,
                            reporting_year_value: null,
                            base_year: target.base_year,
                            base_year_value: null,
                            target_year: target.target_year,
                            target_year_value: await this.stringifyNumericValues(target.target_value),
                            text_box: null,
                            web_link: null,
                            description: target.target_descriptipn,
                            use_supplier_name: target.use_supplier_name,
                            created_by: this.$user.user_id
                        }

                        await this.createAssignedTarget(params);
                        await this.setSupplierTargetCreateModal(false);

                        this.$toast.open({
                            message: i18n.t('dynamicTargets.supplier_targets_created'),
                            type: "success",
                            duration: 3000,
                            dismissable: true
                        })
                    } else {
                        console.log("Target: ", target)
                        if (
                            target.target_year == null
                        ) { console.log("return"); return; }

                        const params = {
                            dynamic_target_id: this.dynamicTarget.dynamic_target_id,
                            assigner_company_id: this.$user.company_id,
                            assignee_company_id: target.assignee_company_id,
                            reporting_year_value: null,
                            base_year: target.base_year,
                            base_year_value: null,
                            target_year: target.target_year,
                            target_year_value: await this.stringifyNumericValues(target.target_value),
                            text_box: null,
                            web_link: null,
                            description: target.target_description,
                            use_supplier_name: target.use_supplier_name,
                            created_by: this.$user.user_id
                        }

                        await this.createAssignedTarget(params);
                        await this.setSupplierTargetCreateModal(false);

                        this.$toast.open({
                            message: i18n.t('dynamicTargets.supplier_targets_created'),
                            type: "success",
                            duration: 3000,
                            dismissable: true
                        })
                    }
                }
            } catch (error) {
                console.error(error.message);
                this.$toast.open({
                    message: i18n.t('dynamicTargets.supplier_targets_not_created'),
                    type: "error",
                    duration: 3000,
                    dismissable: true
                })
            } finally {
                await this._getAssignedTargetsForSuppliers();
            }
        },

        async _createAssignedTargetsForSuppliers() {
            if (
                this.supplierTargetToCreate.supplierTargetYear == null || 
                this.supplierTargetToCreate.supplierTargetValue == null || 
                this.supplierTargetToCreate.supplierBaseYear == null
            ) { return; }
            
            const params = {
                dynamic_target_id: this.dynamicTarget.dynamic_target_id,
                assigner_company_id: this.$user.company_id,
                reporting_year_value: null,
                base_year: this.supplierTargetToCreate.supplierBaseYear,
                base_year_value: null,
                target_year: this.supplierTargetToCreate.supplierTargetYear,
                target_year_value: await this.stringifyNumericValues(this.supplierTargetToCreate.supplierTargetValue),
                description: this.supplierTargetToCreate.supplierTargetDescription,
                use_supplier_name: this.supplierTargetToCreate.useSupplierName,
                created_by: this.$user.user_id
            }

            this.createAssignedTargetsForSuppliers(params)
                .then(async (response) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "success",
                        duration: 5000,
                        dismissible: true,
                    });
                    await this._getAssignedTargetsForSuppliers();
                    await this.setSupplierTargetCreateModal(false);
                })
                .catch(({ response }) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "error",
                        duration: 5000,
                        dismissible: true,
                    });
                })
        },

        async _createTextboxTargetAssignedTargetsForSuppliers() {
            if (
                this.supplierTargetToCreate.supplierTargetYear == null
            ) { return; }

            const params = {
                dynamic_target_id: this.dynamicTarget.dynamic_target_id,
                assigner_company_id: this.$user.company_id,
                reporting_year_value: null,
                base_year: this.supplierTargetToCreate.supplierBaseYear,
                base_year_value: null,
                target_year: this.supplierTargetToCreate.supplierTargetYear,
                target_year_value: await this.stringifyNumericValues(this.supplierTargetToCreate.supplierTargetValue),
                description: this.supplierTargetToCreate.supplierTargetDescription,
                use_supplier_name: this.supplierTargetToCreate.useSupplierName,
                created_by: this.$user.user_id
            }

            this.createAssignedTargetsForSuppliers(params)
                .then(async (response) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "success",
                        duration: 5000,
                        dismissible: true,
                    });
                    await this._getAssignedTargetsForSuppliers();
                    await this.setSupplierTargetCreateModal(false);
                })
                .catch(({ response }) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "error",
                        duration: 5000,
                        dismissible: true,
                    });
                })
        },

        async _updateCustomerAssignedTargetBaseYearValue(target) {
            const params = {
                assigned_target_id: target.assigned_target_id,
                base_year_value: target.base_year_value
            }

            this.updateAssignedTarget(params)
                .then(async (response) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "success",
                        duration: 5000,
                        dismissible: true,
                    });
                    await this._getAssignedTargetsAsSupplier();
                })
                .catch(({ response }) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "error",
                        duration: 5000,
                        dismissible: true,
                    });
                })
        },

        async _updateCustomerTextboxAssignedTargetBaseYearValue(target) {
            const params = {
                assigned_target_id: target.assigned_target_id,
                text_box: target.text_box,
                web_link: target.web_link
            }

            this.updateAssignedTarget(params)
                .then(async (response) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "success",
                        duration: 5000,
                        dismissible: true,
                    });
                    await this._getAssignedTargetsAsSupplier();
                })
                .catch(({ response }) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "error",
                        duration: 5000,
                        dismissible: true,
                    });
                })
        },

        async _updateAssignedTargetForSuppliers () {
            if (!this.supplierTargets || this.supplierTargets.length == 0) {
                return; //handle if the target is not created yet
            }

            const params = {
                dynamic_target_id: this.dynamicTarget.dynamic_target_id,
                assigner_company_id: this.$user.company_id,
                base_year: this.supplierTargetToCreate.supplierBaseYear,
                target_year: this.supplierTargetToCreate.supplierTargetYear,
                target_year_value: this.supplierTargetToCreate.supplierTargetValue,
                description: this.supplierTargetToCreate.supplierTargetDescription
            }

            this.updateAssignedTargetForSuppliers(params)
                .then(async (response) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "success",
                        duration: 5000,
                        dismissible: true,
                    });
                    await this._getAssignedTargetsForSuppliers();
                })
                .catch(({ response }) => {
                    this.$toast.open({
                        message: this.$parseResMessage(response.data.messageName),
                        type: "error",
                        duration: 5000,
                        dismissible: true,
                    });
                })
        },

        async _recalculateValuesAsCustomer(manualUpdate = false) {
            if ((this.companyType == "SUPPLIER" || this.companyType == "BOTH") && this.customerTargets.length > 0 &&  !this.isTextboxTarget) {
                for (const target of this.customerTargets) {
                    const params = {
                        assigned_target_id: target.assigned_target_id
                    }

                    const has_unmatching_value = (target.values_match.base_year == false || target.values_match.reporting_year == false || target.values_match.current_year == false);

                    if (!has_unmatching_value || manualUpdate == true) {
                        await this.recalculateAllValues(params)
                            .then(response => {
                                this.$toast.open({
                                    message: this.$parseResMessage(response.data.messageName),
                                    type: "success",
                                    duration: 5000,
                                    dismissible: true,
                                });
                            })
                            .catch(({ response }) => {
                                this.$toast.open({
                                    message: this.$parseResMessage(response.data.messageName),
                                    type: "error",
                                    duration: 5000,
                                    dismissible: true,
                                });
                            })
                            .finally(async () => {
                                await this._getAssignedTargetsAsSupplier();
                            })   
                    }
                }
            }
        },

        // event handlers //

        async clickRecalculateButton(item) {
            if (item.val == 'base') {
                await this._recalculateBaseYearValue();
            } else if (item.val == 'reporting') {
                await this._recalculateReportingYearValue();
            } else if (item.val == 'current') {
                await this._recalculateCurrentYearValue();
            } else {
                console.error("Invalid item on clickRecalculateButton! \n", item);
            }
        },

        async clickOpenDocuments(item) {
            if (this.assignedTargetValue != null && this.assignedTargetValue.hasOwnProperty("assigned_target_id")) {

                if (item){
                    const year = item.val == "current" ? this.currentYear : this.currentYear -1;   
                    await this.setDocumentsModal(true, year);
                } else {
                    await this.setDocumentsModal(true, 2024);
                }
            }
        },

        async clickOpenReadonlyDocuments() {
            if (this.dynamicTarget != null && this.dynamicTarget.hasOwnProperty("dynamic_target_id"))
            await this.setReadonlyDocumentsModal(true);
        },


        async handleFetchDynamicTarget() {
            if (!this.requiresMetrics) return;

            this.requiredMetrics = [];
            const numbers = this.dynamicTarget.calculation.split("_")[1];
            let calculation_parts;
            if (numbers.includes(",")) {
                calculation_parts = numbers.split(",");
            } else {
                calculation_parts = numbers;
            }

            for (const metricIndex of calculation_parts) {
                const metric = this.filteredMetrics[Number(metricIndex)-1];
                this.requiredMetrics.push(metric);  
            }
        },

        async handleClickMetricName(item) {
            this.$emit("onClickMetricName", item);
        },

        async handleChangeTargetYearProgressiveValue(event) {
            const val = Number(event.target.value);
            if (this.baseYearValue == null) {
                console.warn("No base year value defined");
            }

            if (event.type == "keypress" && event.key == "Enter") {
                event.target.blur().preventDefault();
            }

            const calculation = this.dynamicTarget.calculation_result_type;

            let result = null;
            if (calculation == '%') {
                result = (Number(this.baseYearValue) * val / 100) + Number(this.baseYearValue);
            } else {
                result = (Number(this.baseYearValue) + val);
            }

            if (result == Number(this.targetYearValue)) {
                console.warn("Values are the same");
            }

            this.targetYearValue = result;

            await this._updateAssignedTarget();
        },

        async handleClickCreateMethodTab(tab) {
            await this.setSupplierCreateTab(tab);
            if (tab == "separate") {
                await this.setSeparateSupplierTargets();
            }
        },

        async handleSwitchShownName(item) {
            if (item == "all") {
                this.supplierTargetToCreate.useSupplierName = !this.supplierTargetToCreate.useSupplierName;
            } else {
                item.use_supplier_name = !item.use_supplier_name;
            }
        },

        async handleTargetHasNoMatch() {
            this.$emit("targetHasNoMatch", this.targetHasNoMatch);
        },

        async handleCollapse() {
            this.$emit("collapseTarget");
        },

        // setters //

        async setLoading(bool) {
            this.$emit("isLoading", bool);
            this.loading = bool;
        },

        async resetAssignedTarget() {
            this.assignedTargetValue = null;
            this.targetYear = null;
            this.baseYear = null;
            this.targetYearValue = null;
            this.baseYearValue = null;
            this.reportingYearValue = null;
            this.currentYearValue = null;
            this.textBoxValue = "";
            this.webLinkValue = "";
        },

        async resetSupplierTarget() {
            this.supplierTargets = [];
            this.supplierBaseYear = null;
            this.supplierTargetYear = null;
            this.supplierTargetValue = null;
            this.supplierTargetDescription = null;

            // to create //
            this.supplierTargetToCreate.supplierBaseYear = null;
            this.supplierTargetToCreate.supplierTargetYear = null;
            this.supplierTargetToCreate.supplierTargetValue = null;
            this.supplierTargetToCreate.supplierTargetDescription = null;
        },

        async setDocumentsModal(bool, year) {
            this.documentsModal = bool;
            this.documentsModalYear = year;
        },

        async setReadonlyDocumentsModal(bool) {
            this.readonlyDocumentsModal = bool;
        },

        async setSupplierTargetCreateModal(bool) {
            this.supplierTargetCreateModal = bool;
        },

        async setSupplierCreateTab(tab) {
            this.supplierCreateTab = tab;
        },

        async setSeparateSupplierTargets() {
            let targets = [];

            for (const supplier of this.suppliers) {
                targets.push(
                    {
                        assignee_company_id: supplier.company_id,
                        company_name: supplier.company_name,
                        base_year: this.supplierTargetToCreate.supplierBaseYear,
                        target_year: this.supplierTargetToCreate.supplierTargetYear,
                        target_value: this.supplierTargetToCreate.supplierTargetValue,
                        target_description: this.supplierTargetToCreate.supplierTargetDescription,
                        use_supplier_name: false
                    }
                )
            }

            this.supplierTargetsForSeparateCreate = targets;
        },

        // utils //

        findSuppliersTarget(supplier_id) {
            let target = this.supplierTargets.find(item => item.assignee_company_id == supplier_id);
            
            if (target) {
                if (!this.valueExists(target.current_year_value)) {
                    target.current_year_value = null;
                }

                if (!this.valueExists(target.base_year_value)) {
                    target.base_year_value = null;
                }

                return target;
            } else {
                return {
                    base_year_value: null,
                    current_year_value: null
                };
            }
        },

        convertCurrentStatus(number) {
            if (number <= 0) {
                return 0;
            } else {
                return number;
            }
        },

        getBackgroundColor(row) {
            const colorNegative = "#c1273e";
            const colorPositive = "#73a942";

            const base_val = Number(row.base_year_value);
            const target_val = Number(row.target_year_value);
            let reporting_year_value;
            let current_year_value;
            const calculation = this.dynamicTarget.calculation_result_type;
            if (calculation == '%') {
                reporting_year_value = base_val + base_val * Number(row.reporting_year_value) / 100;
                current_year_value = base_val + base_val * Number(row.current_year_value) / 100;
            } else if (calculation == 'X') {
                reporting_year_value = base_val + Number(row.reporting_year_value);
                current_year_value = base_val + Number(row.current_year_value);
            } else {
                return "lightgray";
            }
            
            const is_increase = this.dynamicTarget.calculation.toLowerCase().includes("increase");

            if (row.target_year >= row.reporting_year) {
                if (target_val > reporting_year_value) {
                    return is_increase ? colorNegative : colorPositive;
                } else {
                    return is_increase ? colorPositive : colorNegative;
                }
            } else if (row.target_year == this.currentYear) {
                if (target_val > current_year_value) {
                    return is_increase ? colorNegative : colorPositive;
                } else {
                    return is_increase ? colorPositive : colorNegative;
                }
            } else {
                return "lightgray";
            }
        },

        getSupplierBgColor(row) {
            const colorNegative = "#c1273e";
            const colorPositive = "#73a942";

            const base_val = Number(row.base_year_value);
            const target_val = Number(this.supplierTargetValue);
            let current_year_value;
            const calculation = this.dynamicTarget.calculation_result_type;
            if (calculation == '%') {
                current_year_value = base_val + base_val * Number(row.current_year_value) / 100;
            } else if (calculation == 'X') {
                current_year_value = base_val + Number(row.current_year_value);
            } else {
                return "lightgray";
            }
            
            const is_increase = this.dynamicTarget.calculation.toLowerCase().includes("increase");

            if (Number(this.supplierTargetYear) >= this.currentYear) {
                if (target_val > current_year_value) {
                    return is_increase ? colorNegative : colorPositive;
                } else {
                    return is_increase ? colorPositive : colorNegative;
                }
            } else {
                return "lightgray";
            }
        },
        
        async stringifyNumericValues(value) {
            if (value == null || value == undefined) {
                return null;
            } else {
                return String(value);
            }
        },

        fixFloatingToFour(value) {
            if (String(value).includes(".")) {
                return Number(value).toFixed(4);
            } else {
                return Number(value);
            }
        },

        valueExists(value) {
            if (value == null || value == undefined || value == false || value == "false" || value == "null" || value == "undefined") {
                return false;
            } else {
                return true;
            }
        },

        hasUnmatchingValue(item) {
            const has_unmatching_value = (item.values_match.base_year == false || item.values_match.reporting_year == false || item.values_match.current_year == false);
            return has_unmatching_value;
        }
    }
}
</script>

<template>
    <div class="section-container small-box-shadow" v-if="!loading">
        <div class="collapse-target-xmark flex-all-centered-row" style="justify-content: flex-end;"
            @click="handleCollapse()" 
        >
            <i class="fa-solid fa-xmark"></i>
        </div>

        <div class="top-section">
            <div class="left-section">
                <div class="switch-ctr">
                    <switch-button v-if="assignedTargetValue" @buttonState="useAsCompanyTarget" :initialValue="true"/>
                    <switch-button v-else @buttonState="useAsCompanyTarget" :initialValue="false"/>
                    <div>
                        {{ $t('dynamicTargets.use_as') }}
                    </div>
                </div>

                <div class="target-desc">
                    <div class="inlined-field" v-if="!isTextboxTarget">
                        <span style="display: inline-block; vertical-align: middle;">
                            {{ formattedDynamicTargetName[0] }}
                        </span>
                        
                        <input v-model="targetYearProgressiveValue" type="number" 
                            @keypress.enter="handleChangeTargetYearProgressiveValue($event)"
                            @blur="handleChangeTargetYearProgressiveValue($event)"
                        >

                        <span style="display: inline-block; vertical-align: middle;">
                            {{ formattedDynamicTargetName[1] }}
                        </span>
                    </div>
                    <div v-else>
                        {{ dynamicTarget.dynamic_target_name }}
                    </div>
                </div>

                <div class="required-metrics-ctr" v-if="requiresMetrics">
                    <br><br>
                    <div class="header">
                        {{ $t('dynamicTargets.required_metrics') }}:
                    </div>
                    <div v-for="item in requiredMetrics" :key="item.metric_kpi_id" class="required-metric-name"
                        @click="handleClickMetricName(item)"
                    >
                        <i v-if="item.has_input == false" class="fa-solid fa-circle-exclamation"></i><span>{{ item.metric_kpi_name }}</span>
                    </div>
                </div>
            </div>

            <div class="right-section">
                <div class="left-table small-table" v-if="!isTextboxTarget">
                    <div class="--ctr header-ctr">
                        <div class="header row-item" style="position: relative;" v-for="item in leftTableHeaders" :key="item">
                            {{ item.text }}
                            <i @click="clickRecalculateButton(item)" v-b-tooltip.hover="{title: $t('dynamicTargets.recalculate_tooltip')}" 
                                v-if="item.val == 'base' && targetValuesMatch[0] == false" class="fa-solid fa-calculator"
                            >
                            </i>
                        </div>
                    </div>
                    <div class="--ctr">
                        <div class="year-select-ctr row-item">
                            <select class="year-select" v-model="baseYear" @change="_updateAssignedTarget()">
                                <option :value="year.val" v-for="year in pastYears" :key="year.val">
                                    {{ year.text }}
                                </option>
                            </select>
                        </div>

                        <div class="year-select-ctr row-item">
                            <select class="year-select" v-model="targetYear" @change="_updateAssignedTarget()">
                                <option :value="year.val" v-for="year in nextYears" :key="year.val">
                                    {{ year.text }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div class="--ctr">
                        <div class="input-ctr row-item" style="position: relative;">
                            <input @change="_updateAssignedTarget()" type="number" placeholder="-" v-model="baseYearValue">
                            <i v-if="targetValuesMatch[0] == false" class="fa-solid fa-circle-exclamation value-match-indicator"
                                v-b-tooltip.hover.bottom="{title: $t('dynamicTargets.no_match')}"
                            ></i>
                        </div>
                        <div class="input-ctr row-item">
                            <input @change="_updateAssignedTarget()" type="number" placeholder="-" v-model="targetYearValue">
                        </div>
                    </div>
                </div>

                <div class="right-table small-table" v-if="!isTextboxTarget">
                    <div class="--ctr header-ctr">
                        <div class="header row-item" v-for="(item, index) in rightTableHeaders" :key="index">
                            {{ item.text }} ({{ calculationType }})

                            <mixed-icons v-if="assignedTargetValue"
                                :hover="true"
                                :name="'fa-regular fa-folder-open'" 
                                :main_color="'whitesmoke'"
                                :side_class="
                                    targetDocumentStatus.some(
                                    status => 
                                        status.year == (item.val == 'current' ? currentYear : currentYear -1) 
                                        && status.has_doc
                                    ) 
                                    ? 'fa-solid fa-check' 
                                    : null
                                "
                                :side_color="'var(--sustainfinity-green)'"
                                @click="clickOpenDocuments(item)"
                                class="target-doc-ico-btn"
                            />
                            <i @click="clickRecalculateButton(item)" v-b-tooltip.hover="{title: $t('dynamicTargets.recalculate_tooltip')}" class="fa-solid fa-calculator"
                                v-if="targetValuesMatch[index + 1] == false"
                            >
                            </i>
                        </div>
                    </div>
                    <div class="--ctr">
                        <div class="year-select-ctr row-item">
                            {{ currentYear-1 }}
                        </div>
                        <div class="year-select-ctr row-item">
                            {{ currentYear }}
                        </div>
                    </div>
                    <div class="--ctr">
                        <div class="input-ctr row-item" style="position: relative;">
                            <input @change="_updateAssignedTarget()" type="number" placeholder="-" v-model="reportingYearValue">
                            <i v-if="targetValuesMatch[1] == false" class="fa-solid fa-circle-exclamation value-match-indicator"
                                v-b-tooltip.hover.bottom="{title: $t('dynamicTargets.no_match')}"
                            ></i>
                        </div>
                        <div class="input-ctr row-item" style="position: relative;">
                            <input @change="_updateAssignedTarget()" type="number" placeholder="-" v-model="currentYearValue">
                            <i v-if="targetValuesMatch[2] == false" class="fa-solid fa-circle-exclamation value-match-indicator"
                                v-b-tooltip.hover.bottom="{title: $t('dynamicTargets.no_match')}"
                            ></i>
                        </div>
                    </div>
                </div>

                <div class="text-box-input-container" v-if="isTextboxTarget">
                    <div class="text-box-group">
                        <textarea
                            v-model="textBoxValue"
                            :placeholder="$t('dynamicTargets.textbox_placeholder')"
                        >
                        </textarea>
                        <input type="url" v-model="webLinkValue" :placeholder="$t('dynamicTargets.weblink_placeholder')">

                        <i class="fa-solid fa-folder-open target-documents-button" style="color: gray" @click="clickOpenDocuments()"></i>
                        <i v-if="targetDocumentStatus.some(
                            status => 
                                status.year == currentYear
                                && status.has_doc
                            ) " 
                            class="fa-solid fa-check"
                            style="position: absolute; right: .15em; bottom: .15em; color: var(--sustainfinity-green);"
                        >
                        </i>
                    </div>
                    <button class="update-textbox-button" @click="_updateAssignedTarget()">
                        {{ $t('dynamicTargets.update_text_answer') }}
                    </button>
                </div>
            </div>
        </div>

        <div class="bottom-section" v-if="(companyType == 'REGULAR' || companyType == 'SUPPLIER' || companyType == 'BOTH')">

            <!-- this entire element used to be wrapped inside a pop-up modal, moved it into the main frame due to design choices -->
            <div class="suppliers-modal-body" v-if="(companyType == 'REGULAR' || companyType == 'BOTH')">
                <div class="top-section">
                    <div class="title">
                        {{ $t('dynamicTargets.set_for_suppliers') }}
                    </div>
                    <div class="target-name">
                        <!-- this will have a different type of name -->
                        {{ dynamicTarget.dynamic_target_name }}
                    </div>
                </div>

                <div class="supplier-table" v-if="!isTextboxTarget">
                    <div class="headers-ctr">
                        <div class="header" v-for="(item, index) in supplierCreateTable_Modal" :key="index" :style="{flex: item.flex}">
                            {{ item.text }}
                        </div>
                    </div>

                    <div class="rows-ctr">
                        <div class="supplier-table-row">
                            <div class="row-cell" :style="{flex: supplierCreateTable_Modal[0].flex}">
                                <select class="year-select" v-model="supplierTargetToCreate.supplierBaseYear" @change="_updateAssignedTargetForSuppliers()">
                                    <option :value="year.val" v-for="year in pastYears" :key="year.val">
                                        {{ year.text }}
                                    </option>
                                </select>
                            </div>
                            <div class="row-cell" :style="{flex: supplierCreateTable_Modal[1].flex}">
                                <select class="year-select" v-model="supplierTargetToCreate.supplierTargetYear" @change="_updateAssignedTargetForSuppliers()">
                                    <option :value="year.val" v-for="year in nextYears" :key="year.val">
                                        {{ year.text }}
                                    </option>
                                </select>
                            </div>
                            <div class="row-cell" :style="{flex: supplierCreateTable_Modal[2].flex}">
                                <input :placeholder="$t('dynamicTargets.set_a_target_for_suppliers')" type="number" v-model="supplierTargetToCreate.supplierTargetValue" @change="_updateAssignedTargetForSuppliers()">
                            </div>
                            <div class="row-cell" :style="{flex: supplierCreateTable_Modal[3].flex}">
                                <input :placeholder="$t('dynamicTargets.write_brief_description')" type="text" v-model="supplierTargetToCreate.supplierTargetDescription" @change="_updateAssignedTargetForSuppliers()">
                            </div>
                        </div>
                    </div>
                </div>

                <div class="supplier-table" v-if="isTextboxTarget">
                    <div class="headers-ctr">
                        <div class="header" v-for="(item, index) in supplierTextboxCreateTable_Modal" :key="index" :style="{flex: item.flex}">
                            {{ item.text }}
                        </div>
                    </div>

                    <div class="rows-ctr">
                        <div class="supplier-table-row">
                            <div class="row-cell" :style="{flex: supplierTextboxCreateTable_Modal[0].flex}">
                                <select class="year-select" v-model="supplierTargetToCreate.supplierTargetYear" @change="_updateAssignedTargetForSuppliers()">
                                    <option :value="year.val" v-for="year in nextYears" :key="year.val">
                                        {{ year.text }}
                                    </option>
                                </select>
                            </div>
                            <div class="row-cell" :style="{flex: supplierTextboxCreateTable_Modal[1].flex}">
                                <input :placeholder="$t('dynamicTargets.write_brief_description')" type="text" 
                                    v-model="supplierTargetToCreate.supplierTargetDescription" @change="_updateAssignedTargetForSuppliers()"
                                >
                            </div>
                        </div>
                    </div>
                </div>

                <button v-if="!supplierTargetsExists && !isTextboxTarget"
                    class="assign-for-suppliers-btn" :class="{'disabled-btn': assignForSuppliersDisabled}" 
                    :disabled="assignForSuppliersDisabled" @click="setSupplierTargetCreateModal(true)"
                >
                    {{ $t('dynamicTargets.assign_target') }}
                </button>

                <button v-if="!supplierTargetsExists && isTextboxTarget"
                     class="assign-for-suppliers-btn" :class="{'disabled-btn': assignForSuppliersDisabled}" 
                    :disabled="assignForSuppliersDisabled" @click="setSupplierTargetCreateModal(true)"
                >
                    {{ $t('dynamicTargets.assign_target') }}
                </button>
                
                <div class="separator" v-if="supplierTargetsExists">
                    <!-- just a line -->
                </div>

                <div class="supplier-table" v-if="supplierTargetsExists && !isTextboxTarget">
                    <div class="headers-ctr">
                        <div class="header" v-for="(item, index) in supplierMonitorTable_Modal" :key="index" :style="{flex: item.flex}">
                            {{ item.text }}
                        </div>
                    </div>

                    <div class="rows-ctr">
                        <div class="supplier-table-row" v-for="(supplier, index) in supplierTargets" :key="index">
                            <div class="row-cell" :style="{flex: supplierMonitorTable_Modal[0].flex}">
                                {{ supplier.assignee_company_name }}
                            </div>
                            <div class="row-cell" :style="{flex: supplierMonitorTable_Modal[1].flex, backgroundColor: getSupplierBgColor(findSuppliersTarget(supplier.assignee_company_id))}">
                                {{ findSuppliersTarget(supplier.assignee_company_id).base_year_value }}
                            </div>
                            <div class="row-cell" :style="{flex: supplierMonitorTable_Modal[2].flex, backgroundColor: getSupplierBgColor(findSuppliersTarget(supplier.assignee_company_id))}"
                            >
                                {{ findSuppliersTarget(supplier.assignee_company_id).current_year_value }}
                            </div>
                        </div>
                    </div>
                </div>

                <div class="text-answers-ctr" v-if="supplierTargetsExists && isTextboxTarget">
                    <div class="text-answer" v-for="item in supplierTargets" :key="item.assignee_company_id">
                        <div class="text-header">
                            {{ item.assignee_company_name }}
                        </div>
                        <div class="text-answer">
                            <span class="name">{{ $t('dynamicTargets.answer') }}: </span>
                            {{ item.text_box ? item.text_box : null }}
                        </div>
                        <div class="text-weblink">
                            <span class="name">{{ $t('dynamicTargets.weblink') }}: </span>
                            <a :href="item.web_link" target="_blank">{{ item.web_link ? item.web_link : null }}</a>
                        </div>
                    </div>
                </div>
            </div>

            <div class="customer-assigned-targets-section" v-if="(companyType == 'SUPPLIER' || companyType == 'BOTH') && customerTargets.length > 0 && !isTextboxTarget">
                <div class="supplier-table-ctr" v-for="row in customerTargets" :key="row">
                    <div class="supplier-target-name">
                        {{ 
                            row.use_supplier_name 
                            ? dynamicTarget.dynamic_supplier_target_name
                            : dynamicTarget.dynamic_target_name
                        }}
                    </div>
                    
                    <div class="supplier-table" style="width: 100%;">
                        <div class="headers-ctr">
                            <div class="header" v-for="(item, index) in customerTableHeaders" :key="index" :style="{flex: item.flex}">
                                {{ item.text }}
                                <i class="fa-solid fa-calculator recalculate-btn"
                                    v-if="index == 2 && customerTargetHasNoMatch"
                                    @click="_recalculateValuesAsCustomer(true)"
                                >
                                </i>
                            </div>
                        </div>

                        <div class="rows-ctr">
                            <div class="supplier-table-row">
                                <div class="row-cell" :style="{flex: customerTableHeaders[0].flex}">
                                    {{ row.assigner_company_name }}
                                </div>
                                <div class="row-cell" :style="{flex: customerTableHeaders[1].flex}">
                                    {{ row.base_year }}
                                </div>
                                <div class="row-cell" :style="{flex: customerTableHeaders[2].flex}">
                                    <input type="number" v-model="row.base_year_value" 
                                        :placeholder="$t('dynamicTargets.enter_value')"
                                        @change="_updateCustomerAssignedTargetBaseYearValue(row)"
                                    >
                                    <i v-if="hasUnmatchingValue(row)" class="fa-solid fa-circle-exclamation value-match-indicator"
                                        v-b-tooltip.hover.bottom="{title: $t('dynamicTargets.no_match')}"
                                    >
                                    </i>
                                </div>
                                <div class="row-cell" :style="{flex: customerTableHeaders[3].flex}">
                                    {{ row.target_year }}
                                </div>
                                <div class="row-cell" style="color: whitesmoke !important;" 
                                    :style="{flex: customerTableHeaders[4].flex, backgroundColor: getBackgroundColor(row)}"
                                >
                                    {{ row.target_year_value }}
                                </div>
                                <div class="row-cell" :style="{flex: customerTableHeaders[5].flex}">
                                    {{ row.description }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="customer-assigned-targets-section" v-if="(companyType == 'SUPPLIER' || companyType == 'BOTH') && customerTargets.length > 0 && isTextboxTarget">
                <div class="text-answers-ctr">
                    <div class="text-answer" v-for="item in customerTargets" :key="item.assignee_company_id">
                        <div class="text-header">
                            <span class="name">{{ $t('dynamicTargets.customer') }}: </span>
                            <span style="color: slategray;">{{ item.assigner_company_name }} ({{ $t('dynamicTargets.due') }} {{ item.target_year }})</span>
                        </div>
                        <div class="text-answer">
                            <textarea :placeholder="$t('dynamicTargets.textbox_placeholder')" class="customer-answer-input" 
                                rows="3" v-model="item.text_box" @change="_updateCustomerTextboxAssignedTargetBaseYearValue(item)"
                            >
                            </textarea>
                        </div>
                        <div class="text-weblink">
                            <input :placeholder="$t('dynamicTargets.weblink_placeholder')" class="customer-answer-input" 
                                type="url" v-model="item.web_link" @change="_updateCustomerTextboxAssignedTargetBaseYearValue(item)"
                            >
                        </div>
                    </div>
                </div>
            </div>
        
        </div>

        <b-modal
            hide-header hide-footer
            size="xl" centered
            v-model="supplierTargetCreateModal"
        >
            <div class="flex-all-centered-col">
                <div class="supplier-create-tabs">
                    <div class="tab-button" :class="{'selected-tab-button': supplierCreateTab == 'all'}" 
                        @click="handleClickCreateMethodTab('all')"
                    >
                        {{ $t('dynamicTargets.create_for_all') }}
                    </div>
                    <div class="tab-button" :class="{'selected-tab-button': supplierCreateTab == 'separate'}" 
                        @click="handleClickCreateMethodTab('separate')"
                    >
                        {{ $t('dynamicTargets.create_separately') }}
                    </div>
                </div>

                <div v-if="supplierCreateTab == 'all'"
                    class="flex-all-centered-col" style="padding-top: 1em;"
                >
                    <div class="supplier-table" v-if="isTextboxTarget">
                        <div class="headers-ctr">
                            <div class="header" style="flex: .3; max-width: 30%; position: relative;">
                                {{ $t('dynamicTargets.dynamic_target_name') }}
                                <i class="fa-solid fa-arrow-right-arrow-left switch-names-icon"
                                    :title="$t('dynamicTargets.switch_name')"
                                    @click="handleSwitchShownName('all')"
                                >
                                </i>
                            </div>
                            <div class="header" v-for="(item, index) in supplierTextboxCreateTable_Modal_All" :key="index" :style="{flex: item.flex}">
                                {{ item.text }}
                            </div>
                        </div>

                        <div class="rows-ctr">
                            <div class="supplier-table-row">
                                <div class="row-cell" style="flex: .3; max-width: 30%;">
                                    {{ 
                                        supplierTargetToCreate.useSupplierName 
                                        ? dynamicTarget.dynamic_supplier_target_name 
                                        : dynamicTarget.dynamic_target_name 
                                    }}
                                </div>
                                <div class="row-cell" :style="{flex: supplierTextboxCreateTable_Modal_All[0].flex}">
                                    <select class="year-select" v-model="supplierTargetToCreate.supplierTargetYear" @change="_updateAssignedTargetForSuppliers()">
                                        <option :value="year.val" v-for="year in nextYears" :key="year.val">
                                            {{ year.text }}
                                        </option>
                                    </select>
                                </div>
                                <div class="row-cell" :style="{flex: supplierTextboxCreateTable_Modal_All[1].flex}">
                                    <input :placeholder="$t('dynamicTargets.write_brief_description')" type="text" 
                                        v-model="supplierTargetToCreate.supplierTargetDescription" @change="_updateAssignedTargetForSuppliers()"
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                        
                    <div class="supplier-table" v-if="!isTextboxTarget">
                        <div class="headers-ctr">
                            <div class="header" style="flex: .25; max-width: 25%; position: relative;">
                                {{ $t('dynamicTargets.dynamic_target_name') }}
                                <i class="fa-solid fa-arrow-right-arrow-left switch-names-icon"
                                    :title="$t('dynamicTargets.switch_name')"
                                    @click="handleSwitchShownName('all')"
                                >
                                </i>
                            </div>
                            <div class="header" v-for="(item, index) in supplierCreateTable_Modal_All" :key="index" :style="{flex: item.flex}">
                                {{ item.text }}
                            </div>
                        </div>

                        <div class="rows-ctr">
                            <div class="supplier-table-row">
                                <div class="row-cell" style="flex: .25; max-width: 25%;">
                                    {{ 
                                        supplierTargetToCreate.useSupplierName 
                                        ? dynamicTarget.dynamic_supplier_target_name 
                                        : dynamicTarget.dynamic_target_name 
                                    }}
                                </div>
                                <div class="row-cell" :style="{flex: supplierCreateTable_Modal_All[0].flex}">
                                    <select class="year-select" v-model="supplierTargetToCreate.supplierBaseYear" @change="_updateAssignedTargetForSuppliers()">
                                        <option :value="year.val" v-for="year in pastYears" :key="year.val">
                                            {{ year.text }}
                                        </option>
                                    </select>
                                </div>
                                <div class="row-cell" :style="{flex: supplierCreateTable_Modal_All[1].flex}">
                                    <select class="year-select" v-model="supplierTargetToCreate.supplierTargetYear" @change="_updateAssignedTargetForSuppliers()">
                                        <option :value="year.val" v-for="year in nextYears" :key="year.val">
                                            {{ year.text }}
                                        </option>
                                    </select>
                                </div>
                                <div class="row-cell" :style="{flex: supplierCreateTable_Modal_All[2].flex}">
                                    <input :placeholder="$t('dynamicTargets.set_a_target_for_suppliers')" type="number" v-model="supplierTargetToCreate.supplierTargetValue" @change="_updateAssignedTargetForSuppliers()">
                                </div>
                                <div class="row-cell" :style="{flex: supplierCreateTable_Modal_All[3].flex}">
                                    <input :placeholder="$t('dynamicTargets.write_brief_description')" type="text" v-model="supplierTargetToCreate.supplierTargetDescription" @change="_updateAssignedTargetForSuppliers()">
                                </div>
                            </div>
                        </div>
                    </div>

                    <button
                        class="assign-for-suppliers-btn--modal" :class="{'disabled-btn': assignForSuppliersDisabled}"
                        :disabled="assignForSuppliersDisabled" @click="createTargetForSuppliers()"
                    >
                        {{ $t('dynamicTargets.assign_target') }}
                    </button>
                </div>

                <div v-if="supplierCreateTab == 'separate'"
                    class="flex-all-centered-col" style="padding-top: 1em;"
                >
                    <div class="flex-all-centered-col" v-if="isTextboxTarget">
                        <div class="supplier-table" style="margin-bottom: .66em;" v-for="target in supplierTargetsForSeparateCreate" :key="target.assignee_company_id">
                            <div class="headers-ctr">
                                <div class="header" style="flex: .2; max-width: 20%;">
                                    {{ $t('dynamicTargets.company_name') }}
                                </div>
                                <div class="header" style="flex: .2; max-width: 20%; position: relative;">
                                    {{ $t('dynamicTargets.dynamic_target_name') }}
                                    <i class="fa-solid fa-arrow-right-arrow-left switch-names-icon"
                                        :title="$t('dynamicTargets.switch_name')"
                                        @click="handleSwitchShownName(target)"
                                    >
                                    </i>
                                </div>
                                <div class="header" v-for="(item, index) in supplierTextboxCreateTable_Modal_Separate" :key="index" :style="{flex: item.flex}">
                                    {{ item.text }}
                                </div>
                            </div>

                            <div class="rows-ctr">
                                <div class="supplier-table-row">
                                    <div class="row-cell" style="flex: .2; max-width: 20%;">
                                        {{ target.company_name }}
                                    </div>
                                    <div class="row-cell" style="flex: .2; max-width: 20%;">
                                        {{ 
                                            target.use_supplier_name 
                                            ? dynamicTarget.dynamic_supplier_target_name 
                                            : dynamicTarget.dynamic_target_name 
                                        }}
                                    </div>
                                    <div class="row-cell" :style="{flex: supplierTextboxCreateTable_Modal_Separate[0].flex}">
                                        <select class="year-select" 
                                            v-model="target.target_year" 
                                            
                                        >
                                            <option :value="year.val" v-for="year in nextYears" :key="year.val">
                                                {{ year.text }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="row-cell" :style="{flex: supplierTextboxCreateTable_Modal_Separate[1].flex}">
                                        <input :placeholder="$t('dynamicTargets.write_brief_description')" type="text" 
                                            v-model="target.target_description" 
                                        >
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                      
                    <div class="flex-all-centered-col" v-if="!isTextboxTarget">
                        <div class="supplier-table" style="margin-bottom: .66em;" v-for="target in supplierTargetsForSeparateCreate" :key="target.assignee_company_id">
                            <div class="headers-ctr">
                                <div class="header" style="flex: .15; max-width: 15%;">
                                    {{ $t('dynamicTargets.company_name') }}
                                </div>
                                <div class="header" style="flex: .15; max-width: 15%; position: relative;">
                                    {{ $t('dynamicTargets.dynamic_target_name') }}
                                    <i class="fa-solid fa-arrow-right-arrow-left switch-names-icon"
                                        :title="$t('dynamicTargets.switch_name')"
                                        @click="handleSwitchShownName(target)"
                                    >
                                    </i>
                                </div>
                                <div class="header" v-for="(item, index) in supplierCreateTable_Modal_Separate" :key="index" :style="{flex: item.flex}">
                                    {{ item.text }}
                                </div>
                            </div>

                            <div class="rows-ctr">
                                <div class="supplier-table-row">
                                    <div class="row-cell" style="flex: .15; max-width: 15%;">
                                        {{ target.company_name }}
                                    </div>
                                    <div class="row-cell" style="flex: .15; max-width: 15%;">
                                        {{ 
                                            target.use_supplier_name 
                                            ? dynamicTarget.dynamic_supplier_target_name 
                                            : dynamicTarget.dynamic_target_name 
                                        }}
                                    </div>
                                    <div class="row-cell" :style="{flex: supplierCreateTable_Modal_Separate[0].flex}">
                                        <select class="year-select" v-model="target.base_year" >
                                            <option :value="year.val" v-for="year in pastYears" :key="year.val">
                                                {{ year.text }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="row-cell" :style="{flex: supplierCreateTable_Modal_Separate[1].flex}">
                                        <select class="year-select" v-model="target.target_year" >
                                            <option :value="year.val" v-for="year in nextYears" :key="year.val">
                                                {{ year.text }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="row-cell" :style="{flex: supplierCreateTable_Modal_Separate[2].flex}">
                                        <input :placeholder="$t('dynamicTargets.set_a_target_for_suppliers')" type="number" v-model="target.target_value" >
                                    </div>
                                    <div class="row-cell" :style="{flex: supplierCreateTable_Modal_Separate[3].flex}">
                                        <input :placeholder="$t('dynamicTargets.write_brief_description')" type="text" v-model="target.target_description" >
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button
                        class="assign-for-suppliers-btn--modal" :class="{'disabled-btn': assignForSuppliersDisabled}"
                        :disabled="assignForSuppliersDisabled" @click="createTargetForSuppliersSeparately()"
                    >
                        {{ $t('dynamicTargets.assign_target') }}
                    </button>
                </div>
            </div>
        </b-modal>

        <div class="collapse-target flex-all-centered-row" @click="handleCollapse()">
            {{ $t('dynamicTargets.hide') }}
            <i class="fa-solid fa-angle-up"></i>
        </div>
    </div>
</template>

<style lang='scss' scoped>
    .section-container {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-content: center;
        align-items: stretch;
        width: 100%;
        background-color: whitesmoke;
        border-radius: 1em;

        overflow-x: hidden;

        > .top-section {
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-content: center;
            align-items: stretch;
            width: 100%;
            border-bottom: medium solid #d4dddf;
        }

        > .bottom-section {
            padding: 1em;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-content: center;
            align-items: stretch;
            width: 100%;

            > .bottom-section-buttons {
                padding: 1em;
                display: flex;
                flex-direction: flex;
                justify-content: flex-end;
                align-content: center;
                align-items: stretch;
                width: 100%;

                > button {
                    border: 0 !important;
                    outline: 0;
                    border-radius: .8em;
                    padding: .5em;
                    background: linear-gradient(to right, var(--sustainfinity-green), var(--sustainfinity-blue));
                    color: white;
                }
            }
        }
    }

    .left-section {
        padding: 1em;

        width: 40%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-content: flex-start;
        align-items: flex-start;
        border-right: medium solid #d4dddf;
        padding-left: 1em;
        color: black;
        
        > .switch-ctr {
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-content: center;
            align-items: center;
            font-weight: bold;
            font-size: 1.2em;
            margin-bottom: 1em;

            > div {
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-content: center;
                align-items: center;
            }
        }

        > .target-desc {
            font-weight: bold;
            padding-bottom: 1em;


            > .inlined-field {
                > span, input {
                    display: inline !important;
                }

                > input {
                    border-bottom: thin solid gray !important;
                    width: auto !important;
                    text-align: center !important;
                }
            }
        }
    }

    .right-section {
        padding: 1em;
        //padding-bottom: 70px;

        display: flex;
        flex-direction: row;
        justify-content: space-around;
        align-content: center;
        align-items: flex-start;
        width: 100%;

        > .small-table {
            border: thin solid var(--sustainfinity-navy);
            border-radius: 1em;

            width: 45%;

            display: flex;
            flex-direction: column;
            justify-content: center;
            align-content: center;
            align-items: center;

            overflow: hidden;

            > .header-ctr {
                background-color: var(--sustainfinity-navy) !important;
            }

            > .--ctr {
                background-color: whitesmoke;
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-content: center;
                align-items: center;
                text-align: center;
                width: 100%;

                > .header {
                    width: 100%;
                    color: whitesmoke;
                    border-bottom: thin solid lightgray;
                    &:first-of-type {
                        border-right: thin solid lightgray !important;
                    }

                    position: relative;

                    > i {
                        position: absolute;
                        right: .5em;
                        bottom: .5em;

                        transition: all 100ms;
                        cursor: pointer;
                        &:hover {
                            color: lightgray;
                        }
                    }

                    > .target-doc-ico-btn {
                        position: absolute;
                        left: .5em;
                        bottom: .5em;
                        scale: .5;

                        transform: translate(-50%, 40%);
                    }
                }

                > .year-select-ctr {
                    width: 100%;
                    color: black;
                    border-bottom: thin solid lightgray;
                    &:first-of-type {
                        border-right: thin solid var(--sustainfinity-navy);
                    }
                }

                > .input-ctr {
                    &:first-of-type {
                        border-right: thin solid var(--sustainfinity-navy);
                    }

                    width: 100%;

                    > .value-match-indicator {
                        position: absolute;
                        left: 5%;
                        top: 5%;
                        color: var(--invalid-input);
                    }
                }
            }
        }

        > .text-box-input-container {
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-content: flex-end;
            align-items: flex-end;
            width: 100%;
            height: 100%;

            > .text-box-group {
                min-height: 100px !important;
                max-height: 800px !important;
                width: 60%;
                height: 100%;
                outline: thin solid darkgray;
                border: 0;
                border-radius: .5em;

                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                align-content: center;
                align-items: center;
                > textarea {
                    width: 100%;
                    height: 100% !important;
                    border: 0 !important;
                    outline: 0 !important;
                    padding: .25em;
                    background-color: whitesmoke;
                }
                > input {
                    width: 100%;
                    height: 2.5em !important;
                    border: 0 !important;
                    outline: 0 !important;
                    padding: .25em;
                    background-color: whitesmoke;
                    text-align: left !important;
                    border-top: thin solid gray !important;
                }

                position: relative;
                > .target-documents-button {
                    position: absolute;
                    right: 0.5em;
                    bottom: 0.5em;

                    transition: all 100ms;
                    cursor: pointer;
                    &:hover {
                        filter: brightness(120%);
                    }
                }
            }
        }

        position: relative;
        > .recalculate-buttons-ctr {
            position: absolute;
            bottom: 5px;
            left: 50%;
            transform: translateX(-50%);

            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: space-around;
            align-content: center;
            align-items: center;

            // padding: 0 1em 0 1em;

            > button {
                background-color: var(--sustainfinity-blue);
                padding: .5em;
                border: 0;
                outline: 0;
                color: white;
                border-radius: .5em;

                transition: all 100ms;

                &:focus {
                    outline: thin solid var(--sustainfinity-navy) !important;
                }

                &:active {
                    outline: .15em solid var(--sustainfinity-navy) !important;
                }

                &:hover {
                    filter: brightness(115%);
                }
            }
        }
    }

    .row-item {
        padding: 1em;
    }

    .supplier-table-ctr {
        > .supplier-target-name {
            font-weight: bold;
            font-size: 1.1em;
            color: black;
            padding: 0 0 1em .25em;
        }
    }

    .supplier-table {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-content: center;
        align-items: center;
        text-align: center;
        font-weight: bold;
        width: 80%;
        border-radius: 1em;
        border: thin solid var(--sustainfinity-navy);
        overflow: hidden;

        > .headers-ctr {
            display: flex;
            flex-direction: row;
            justify-content: flex-start;
            align-content: center;
            align-items: stretch;
            width: 100%;
            color: white;
            background-color: var(--sustainfinity-navy);

            > .header {
                overflow-x: hidden;
                border-right: thin solid whitesmoke;
                padding: .5em;
                padding-bottom: 1.5em;

                &:last-of-type {
                    border-right: 0 !important;
                }

                position: relative;
                > .recalculate-btn {
                    font-size: .75em;
                    position: absolute;
                    bottom: 2.5%;
                    right: 2.5%;

                    transition: all 150ms;
                    cursor: pointer;
                    &:hover {
                        color: lightgray;
                    }
                }
            }
        }

        > .rows-ctr {
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-content: center;
            align-items: stretch;
            width: 100%;
            color: black;
            background-color: whitesmoke;

            > .supplier-table-row {
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                align-content: center;
                align-items: stretch;
                width: 100%;
                border-bottom: thin solid lightgray;

                &:last-of-type {
                    border-bottom: 0 !important;
                }

                > .row-cell {
                    overflow-x: hidden;
                    border-right: thin solid var(--sustainfinity-navy);
                    padding: .5em;

                    &:last-of-type {
                        border-right: 0 !important;
                    }

                    position: relative;

                    > .value-match-indicator {
                        position: absolute;
                        left: 5%;
                        top: 50%;
                        transform: translateY(-50%);
                        color: var(--invalid-input);
                    }
                }
            }
        }
    }

    .text-answers-ctr {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-content: flex-start;
        align-items: flex-start;
        text-align: left;

        > .text-answer {
            width: 100%;
            padding-top: 1em;
            padding-bottom: 2em;
            border-bottom: thin solid gray;

            > div {
                padding-top: .5em;
                padding-bottom: .5em;

                > span[class="name"] {
                    font-weight: bold;
                    font-size: 1.1em;
                }

                > a {
                    color: var(--sustainfinity-blue);
                }

                > .customer-answer-input {
                    border: 0;
                    outline: thin solid lightgray !important;
                    width: 50% !important;
                    background-color: white !important;
                    padding: .5em;
                    text-align: left;
                    border-radius: .5em;

                    &:focus {
                        outline: thin solid gray !important;
                    }
                }
            }

            > .text-header {
                font-size: 1.5em;
                font-weight: bold;
                color: var(--sustainfinity-navy);
            }
        }
    }

    .suppliers-modal-body {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-content: center;
        align-items: center;
        width: 100%;
        padding: 1.5em;

        > .top-section {
            margin-bottom: 1em;
            padding-bottom: 1em;
            text-align: left;
            border-bottom: thin solid lightgray;
            width: 100%;
            
            > .title {
                color: black;
                font-size: 1.5em;
                font-weight: bold;
                padding-bottom: 1em;
            }
        }

        > .supplier-table {
            width: 100% !important;
        }

        > .separator {
            margin-top: 2rem;
            margin-bottom: 2rem;
            width: 100%;
            height: 1px !important;
            border-bottom: thin solid lightgray;
        }

        > button {
            margin-top: 1em;
            align-self: flex-end;
            border: 0 !important;
            outline: 0;
            border-radius: .5em;
            padding: .33em;
            background-color: var(--sustainfinity-green);
            color: white;
        }
    }

    .update-textbox-button {
        border: 0 !important;
        outline: 0;
        border-radius: .8em;
        padding: .5em;
        background: linear-gradient(to right, var(--sustainfinity-green), var(--sustainfinity-blue));
        color: white;
        margin-left: 1em;
    }

    .required-metrics-ctr {
        border-top: .2em solid darkgray !important; 
        width: 100%;
        
        > .header {
            font-weight: bold;
            font-size: 1.1em;
            padding-bottom: .5em;
        }
    }

    .required-metric-name {
        display: block;
        padding-bottom: .5em;
        > span {
            display: inline !important;
            transition: all 100ms;
            cursor: pointer;
            &:hover {
                text-decoration: underline;
            }
        }

        > i {
            display: inline !important;
            padding-right: .5em;
            color: var(--invalid-input);
        }
    }

    .assign-for-suppliers-btn {
        padding: .3em .75em .3em .75em !important;
        box-shadow: 0 .1em .5em .1em gray;
        background-color: var(--sustainfinity-green);
        color: white;
        font-weight: bold;

        transition: all 100ms;
        cursor: pointer;
        &:hover {
            filter: brightness(115%);
        }
    }

    .assign-for-suppliers-btn--modal {
        border: 0;
        outline: 0;

        margin-top: 1em;
        align-self: flex-end;

        padding: .3em .75em .3em .75em !important;
        box-shadow: 0 .1em .5em .1em gray;
        background-color: var(--sustainfinity-green);
        color: white;
        font-weight: bold;
        border-radius: 0.5em;

        transition: all 100ms;
        cursor: pointer;
        &:hover {
            filter: brightness(115%);
        }

        &:focus {
            outline: .01em solid black !important;
        }

        &:active {
            outline: thin solid black !important;
        }
    }

    .supplier-create-tabs {
        display: flex;
        flex-direction: row;
        justify-content: start;
        align-content: center;
        align-items: center;

        width: 80%;
        border: .2em solid var(--sustainfinity-navy);
        border-radius: 100px;
        color: whitesmoke;
        font-weight: bold;
        overflow: hidden;

        > .tab-button {
            min-width: 50%;
            background-color: var(--sustainfinity-navy);
            padding: .66em;
            text-align: center;
            transition: all 150ms;
            cursor: pointer;
            &:hover {
                filter: brightness(125%);
            }
        }

        > .selected-tab-button {
            pointer-events: none !important;
            background-color: whitesmoke;
            color: var(--sustainfinity-navy) !important;
        }
    }

    .switch-names-icon {
        position: absolute;
        right: .33em;
        bottom: .33em;

        font-size: .8em;
        color: whitesmoke;

        transition: all 150ms;
        cursor: pointer;
        &:hover {
            color: var(--sustainfinity-blue);
        }
    }

    select {
        width: 100%;
        border: 0 !important;
        outline: 0 !important;
        background: 0 !important;
        text-align: center;
    }

    input {
        width: 100%;
        border: 0 !important;
        outline: 0 !important;
        background: 0 !important;
        text-align: center;
    }

    input[type="number"]::-webkit-outer-spin-button,
    input[type="number"]::-webkit-inner-spin-button {
        appearance: none;
        margin: 0;
    }

    /* collapse target */
    .collapse-target {
        font-weight: bold;
        padding: 1em;

        transition: all 150ms;
        cursor: pointer;
        &:hover {
            background-color: #d4dddf;
        }

        > i {
            line-height: 1.5;
            padding-left: .25em;
        }
    }

    .collapse-target-xmark {
        padding: .5em .5em 0 0;
        font-size: 1.5em;

        > i {
            color: rgb(182, 182, 182);
            transition: all 150ms;
            cursor: pointer;
            &:hover {
                color: gray;
            }
        }
    }
</style>